import { addWeeks } from "date-fns"
import { type SelectEventObject } from "highcharts"

import {
  type CategoryChartSeries,
  type DateChartSeries,
} from "~/components/reusable/graphs/SuperChart"
import { type ChartSeriesStyle } from "~/constants/colors"
import type { ClosingStock } from "~/models/variant"
import {
  convertToStartOfMonthUTC,
  convertToStartOfWeekUTC,
  getStartOfFullMonthDatesBetween,
  getStartOfWeekDatesBetween,
  isFuture,
  startOfDayUTC,
} from "~/utils/datetime"
import { type DateRange } from "~/stores/dateRangeStore"

import { Tooltip } from "~/components/reusable/Tooltip"
import { cn } from "~/utils/helpers"

// Helper function to extract a color from gradient or return the color directly
export function extractColor(colorValue: any): string {
  if (typeof colorValue === "string") {
    return colorValue
  }

  if (colorValue && typeof colorValue === "object") {
    // Handle gradient objects
    if (colorValue.stops && Array.isArray(colorValue.stops)) {
      const firstStop = colorValue.stops[0]
      if (Array.isArray(firstStop) && firstStop.length > 1) {
        return firstStop[1] as string
      }
    }

    // Handle Highcharts pattern-fill color objects: { pattern: { color, ... } }
    if (
      colorValue.pattern &&
      typeof colorValue.pattern === "object" &&
      typeof colorValue.pattern.color === "string"
    ) {
      return colorValue.pattern.color
    }

    // Handle other color objects that might have a color property
    if (colorValue.color && typeof colorValue.color === "string") {
      return colorValue.color
    }
  }

  // Fallback to a default color
  return "#6B7280"
}

export function makeStackTooltipFormatter(
  formatValue: (y: number) => string,
): (this: Highcharts.Point) => string {
  return function (this: Highcharts.Point) {
    const ctx = this as unknown as { points?: Highcharts.Point[]; x?: unknown }
    const header = String(ctx.x ?? "")
    const points = [...(ctx.points ?? [])].sort((a, b) => (b.y ?? 0) - (a.y ?? 0))
    let body = ""
    for (const point of points) {
      const value = typeof point.y === "number" ? formatValue(point.y) : "—"
      const color = extractColor(point.color)
      body += `
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
          <div style="display:flex;align-items:center;">
            ${tooltipSwatch(color)}
            <div style="margin-left:8px;font-weight:400;">${point.series.name}</div>
          </div>
          <span style="text-align:right;font-weight:500;color:var(--color-fg-base);">${value}</span>
        </div>`
    }
    return `<div style="z-index:9999;box-shadow:0px 0px 0px 1px rgba(0,0,0,0.08),0px 4px 8px 0px rgba(0,0,0,0.08),0px 8px 16px 0px rgba(0,0,0,0.08);border-radius:12px;border:1px solid var(--color-border-base);background:var(--color-background-background);overflow:hidden;">
      <div style="padding:8px 12px;"><span style="font-weight:500;white-space:pre-line;">${header}</span></div>
      <div style="border-top:1px;border-color:var(--color-border-base);border-style:dotted;width:100%;"></div>
      <div style="padding:8px 12px;display:flex;flex-direction:column;gap:4px;width:100%;">${body}</div>
    </div>`
  }
}

// Push yellow first (cumulative height) so the red drawn after covers the bottom portion,
// producing the red-on-bottom / yellow-on-top stacked look — both overlay the preorder bar.
export function buildPreorderSeries({
  hasPreOrders,
  hasAtRiskPreOrders,
  hasUnfulfillablePreOrders,
  knownPreOrdersGraphData,
  atRiskPreOrdersGraphData,
  unfulfillablePreOrdersGraphData,
}: {
  hasPreOrders: boolean | undefined
  hasAtRiskPreOrders: boolean | undefined
  hasUnfulfillablePreOrders: boolean | undefined
  knownPreOrdersGraphData: DateChartSeries
  atRiskPreOrdersGraphData: DateChartSeries
  unfulfillablePreOrdersGraphData: DateChartSeries
}): DateChartSeries[] {
  const series: DateChartSeries[] = []
  if (hasPreOrders)
    series.push({ ...knownPreOrdersGraphData, stack: "preorders", stacking: "normal" })
  if (hasPreOrders && hasAtRiskPreOrders)
    series.push({ ...atRiskPreOrdersGraphData, grouping: false })
  if (hasPreOrders && hasUnfulfillablePreOrders)
    series.push({ ...unfulfillablePreOrdersGraphData, grouping: false })
  return series
}

/**
 * The week-snapped range a Highcharts drag-selection covers, or undefined when it starts in the
 * past. A drag can run in either direction, so the axis min/max are not ordered by time.
 */
export const selectedForecastWeekRange = (event: SelectEventObject): DateRange | undefined => {
  const axis = event.xAxis[0]!
  const [from, to] = axis.min < axis.max ? [axis.min, axis.max] : [axis.max, axis.min]

  const start = convertToStartOfWeekUTC(new Date(from))
  const end = addWeeks(convertToStartOfWeekUTC(new Date(to)), 1)

  return isFuture(start) ? { start, end } : undefined
}

/**
 * Dash pattern for a projected series' outline: twelve round dots, matching Figma.
 *
 * Figma exports this as `border: 2px dashed`, but CSS is not usable here — the browser derives
 * its own period from the border width, which on a box this small collapses into about three
 * chunky dashes rather than the ring of dots Figma actually renders.
 *
 * The stroke centerline is a 10×10 rect with r=3, so its perimeter is 4·(10-2·3) + 2π·3 ≈ 34.85;
 * twelve periods of 2.904 divide it evenly, so no dot is clipped at the closing corner. Each
 * dash is ~zero-length and round line caps expand it into a circle whose diameter is the 2px
 * stroke width, leaving ~0.9 of clear space between neighbours.
 */
const dashedSwatchDashArray = "0.01 2.894"

/**
 * The series swatch every legend and tooltip shares: a 12px rounded square (4px radius) with a
 * 2px border in the series color — filled for solid series, dashed outline and no fill for
 * dashed (projected/suggested) series, matching the dashed line on the chart.
 *
 * The rect is inset by half the stroke width so the outer edge lands on the 12×12 box, which is
 * what makes the outer corner radius 4 (3 on the centerline + 1 for the stroke).
 *
 * Use this directly only when the legend renders its own label markup (the doughnut legend
 * appends a value column); otherwise prefer {@link legendSwatch}, which pairs it with a label.
 */
export const legendDot = (color: string, options?: { dashed?: boolean }) => (
  <svg className="w-3 h-3 shrink-0" viewBox="0 0 12 12" aria-hidden>
    <rect
      x="1"
      y="1"
      width="10"
      height="10"
      rx="3"
      fill={options?.dashed ? "none" : color}
      stroke={color}
      strokeWidth="2"
      strokeDasharray={options?.dashed ? dashedSwatchDashArray : undefined}
      strokeLinecap={options?.dashed ? "round" : undefined}
    />
  </svg>
)

/** A {@link legendDot} with its series name, per the Figma chart legend spec. */
export const legendSwatch = (
  name: string,
  color: string,
  options?: { dashed?: boolean; disabled?: boolean },
) => (
  <div className="flex gap-2 items-center">
    {legendDot(color, options)}
    <div
      className={cn(
        "label-sm",
        options?.disabled ? "text-fg-muted group-hover:text-fg-base" : "text-fg-base",
      )}
    >
      {name}
    </div>
  </div>
)

/**
 * {@link legendDot} as a markup string, for Highcharts `useHTML` tooltip formatters. Kept here
 * so the two can't drift apart — a tooltip swatch and a legend swatch for the same series have
 * to be indistinguishable, down to the dash period.
 *
 * Coordinates are absolute rather than scaled through a `viewBox`, because Highcharts' AST
 * sanitizer drops `viewBox`. `rx` and `stroke-dasharray` are allowlisted in
 * `utils/highcharts-config` for the same reason.
 */
export function tooltipSwatch(color: string, dashStyle?: string) {
  const dashed = dashStyle && dashStyle !== "Solid"
  const rect = `<rect x="1" y="1" width="10" height="10" rx="3" fill="${
    dashed ? "none" : color
  }" stroke="${color}" stroke-width="2"${
    dashed ? ` stroke-dasharray="${dashedSwatchDashArray}" stroke-linecap="round"` : ""
  }/>`
  return `<div style="display:flex;align-items:center;flex-shrink:0;"><svg width="12" height="12" style="display:block;">${rect}</svg></div>`
}

/**
 * Builds a date series with every style field of a chart series token applied.
 *
 * Prefer this over spreading style fields by hand: a missing `dashStyle` silently falls
 * back to "Solid", which drops the dashed legend and tooltip swatches that mark a series
 * as projected. Style fields are omitted from the caller's half so they can only come
 * from the token.
 */
export const dateSeries = (
  style: ChartSeriesStyle,
  series: Omit<DateChartSeries, "colors" | "dashStyle" | "pattern">,
): DateChartSeries => ({
  ...series,
  colors: [style.color],
  dashStyle: style.dashStyle,
  pattern: style.pattern,
})

/** Category-axis counterpart to {@link dateSeries}. */
export const categorySeries = (
  style: ChartSeriesStyle,
  series: Omit<CategoryChartSeries, "colors" | "dashStyle">,
): CategoryChartSeries => ({
  ...series,
  colors: [style.color],
  dashStyle: style.dashStyle,
})

export const legendCircle = (name: string, color: string) => legendSwatch(name, color)

export const dashedLegendCircle = (name: string, color: string, tooltip?: string) => (
  <Tooltip title={tooltip}>{legendSwatch(name, color, { dashed: true })}</Tooltip>
)

export type InventoryMetric = "Units" | "Cost"

export function applyMetricToClosingStock(
  perVariantStocks: Array<{ variantId: string; date: Date; quantity: number }>,
  variants: Array<{ variantId: string; costPrice: number | null }>,
  metric: InventoryMetric,
): ClosingStock[] {
  const costPriceById = new Map(variants.map((v) => [v.variantId, v.costPrice ?? 0]))
  const byDate = new Map<number, ClosingStock>()

  for (const row of perVariantStocks) {
    const multiplier = metric === "Cost" ? (costPriceById.get(row.variantId) ?? 0) : 1
    const value = row.quantity * multiplier
    const key = row.date.getTime()
    const existing = byDate.get(key)
    if (existing) {
      existing.quantity += value
    } else {
      byDate.set(key, { date: row.date, quantity: value })
    }
  }

  return Array.from(byDate.values()).sort((a, b) => a.date.getTime() - b.date.getTime())
}

function seriesOnPeriodGrid(
  startDate: Date,
  endDate: Date,
  monthly: boolean,
  valueByPeriodStart: Map<number, number>,
) {
  const dates = monthly
    ? getStartOfFullMonthDatesBetween(startDate, endDate)
    : getStartOfWeekDatesBetween(startDate, endDate)
  return dates.map((startOf) => ({
    milliseconds: startOf.getTime(),
    value: valueByPeriodStart.get(startOf.getTime()) ?? 0,
  }))
}

export function generateInventorySeries(
  startDate: Date,
  endDate: Date,
  values: ClosingStock[],
  monthly: boolean,
) {
  const quantityByDay = new Map<number, number>()
  for (const v of values) {
    const key = startOfDayUTC(v.date).getTime()
    if (!quantityByDay.has(key)) quantityByDay.set(key, v.quantity)
  }
  return seriesOnPeriodGrid(startDate, endDate, monthly, quantityByDay)
}

export function generateOrdersSeries(
  startDate: Date,
  endDate: Date,
  values: {
    date: Date
    quantity: number
  }[],
  monthly: boolean,
) {
  const totalByPeriod = new Map<number, number>()
  for (const v of values) {
    const key = (
      monthly ? convertToStartOfMonthUTC(v.date) : convertToStartOfWeekUTC(v.date)
    ).getTime()
    totalByPeriod.set(key, (totalByPeriod.get(key) ?? 0) + v.quantity)
  }
  return seriesOnPeriodGrid(startDate, endDate, monthly, totalByPeriod)
}

export const hatchedColumn = (style: ChartSeriesStyle) => {
  const fill = style.pattern
    ? {
        pattern: {
          path: { d: style.pattern.path, strokeWidth: style.pattern.strokeWidth },
          width: style.pattern.width,
          height: style.pattern.height,
          color: style.color,
          backgroundColor: "transparent",
        },
      }
    : undefined
  return {
    fill,
    series: fill
      ? {
          color: style.color,
          borderColor: style.color,
          borderWidth: 1,
          custom: { swatchDashStyle: style.dashStyle, hatched: true },
        }
      : { color: style.color },
  }
}
